type FilterValue = string | number | Date | FilterValueObject[];
type FilterValueObject = { label: string; value: FilterValue };

function isValueDifferent(a: FilterValue, b: FilterValue): boolean {
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return true;

    return a.some((itemA, i) => {
      const itemB = b[i];
      return (
        itemA.label !== itemB.label ||
        isValueDifferent(itemA.value, itemB.value)
      );
    });
  }

  if (a instanceof Date && b instanceof Date) {
    return a.getTime() !== b.getTime();
  }

  return a !== b;
}

export function hasDifferentValues(
  arr1: FilterValueObject[],
  arr2: FilterValueObject[],
): boolean {
  if (arr1.length !== arr2.length) return true;

  return arr1.some((itemA, i) => {
    const itemB = arr2[i];
    return (
      itemA.label !== itemB.label || isValueDifferent(itemA.value, itemB.value)
    );
  });
}

export function deepEqual<T>(obj1: T, obj2: T): boolean {
  if (obj1 === obj2) return true;

  if (
    typeof obj1 !== "object" ||
    typeof obj2 !== "object" ||
    obj1 === null ||
    obj2 === null
  ) {
    return false;
  }

  for (const key in obj1) {
    const val1 = obj1[key];
    const val2 = obj2[key];

    const areObjects =
      typeof val1 === "object" &&
      typeof val2 === "object" &&
      val1 !== null &&
      val2 !== null;

    if (areObjects) {
      if (!deepEqual(val1, val2)) {
        return false;
      }
    } else if (val1 !== val2) {
      return false;
    }
  }

  return true;
}

export function mergeFilters<T extends readonly Record<string, string>[]>(
  arr: T,
): Merge<T> {
  return Object.assign({}, ...arr) as Merge<T>;
}

type Merge<T extends readonly Record<string, string>[]> = {
  [K in T[number] as keyof K]: K[keyof K];
};

/**
 * Parses status filter and handles special case for WITH_SUSPENSION
 * @param filterStatus status values as a comma-separated string
 * @returns Object with separate status and suspension status filters
 */
export const parseStatusFilters = (filterStatus?: string) => {
  let suspensionStatusFilter = {};
  let statusFilter = {};

  if (filterStatus) {
    const statusValues = filterStatus.split(",");
    if (statusValues.includes("WITH_SUSPENSION")) {
      // Send suspension status filter
      suspensionStatusFilter = {
        "filter[suspension_status]": "PENDING,APPROVED,DENIED,SCALED",
      };
      // Remove WITH_SUSPENSION from status values and keep other status values
      const otherStatusValues = statusValues.filter(
        (status) => status !== "WITH_SUSPENSION",
      );
      if (otherStatusValues.length > 0) {
        statusFilter = { "filter[status]": otherStatusValues.join(",") };
      }
    } else {
      statusFilter = { "filter[status]": filterStatus };
    }
  }
  return { statusFilter, suspensionStatusFilter };
};
